import { brandStorage as localStorage } from '@/utils/brandSession';
import React, { useState, useEffect } from 'react';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Input } from './ui/input';
import { Copy, Eye, EyeOff, Users, Calendar, FileText, Building, Save, X, Loader2, RefreshCw, KeyRound, LogIn } from 'lucide-react';
import { toast } from 'sonner';
import { API_URL } from '../utils/config';


const OperatorCredentials = () => {
  const [operators, setOperators] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [saving, setSaving] = useState(false);
  const [impersonating, setImpersonating] = useState(null);

  const token = localStorage.getItem('hw_token');

  // Default operators (fallback if no users in DB)
  const defaultOperators = [
    {
      id: 'default-1',
      email: 'prenotazioni@holidayw.eu',
      role: 'bookings_operator',
      roleName: 'Operatore Prenotazioni',
      description: 'Gestisce tutte le prenotazioni in entrata',
      icon: Calendar,
      color: 'bg-blue-500'
    },
    {
      id: 'default-2',
      email: 'customerservice@holidayw.eu',
      role: 'customer_service',
      roleName: 'Customer Service',
      description: 'Gestisce immobili e responsabili',
      icon: Building,
      color: 'bg-emerald-500'
    },
    {
      id: 'default-3',
      email: 'partnerinvoice@holidayw.eu',
      role: 'invoice_operator',
      roleName: 'Operatore Fatture',
      description: 'Gestisce tutte le fatture da pagare',
      icon: FileText,
      color: 'bg-amber-500'
    },
    {
      id: 'default-4',
      email: 'marketing@holidayw.eu',
      role: 'marketing_operator',
      roleName: 'Operatore Marketing',
      description: 'Gestisce partner e property managers',
      icon: Users,
      color: 'bg-purple-500'
    }
  ];

  const roleConfig = {
    'bookings_operator': { icon: Calendar, color: 'bg-blue-500', name: 'Operatore Prenotazioni', desc: 'Gestisce tutte le prenotazioni in entrata' },
    'booking_operator': { icon: Calendar, color: 'bg-blue-500', name: 'Operatore Prenotazioni', desc: 'Gestisce tutte le prenotazioni in entrata' },
    'customer_service': { icon: Building, color: 'bg-emerald-500', name: 'Customer Service', desc: 'Gestisce immobili e responsabili' },
    'invoice_operator': { icon: FileText, color: 'bg-amber-500', name: 'Operatore Fatture', desc: 'Gestisce tutte le fatture da pagare' },
    'marketing_operator': { icon: Users, color: 'bg-purple-500', name: 'Operatore Marketing', desc: 'Gestisce partner e property managers' },
    'marketing': { icon: Users, color: 'bg-purple-500', name: 'Operatore Marketing', desc: 'Gestisce partner e property managers' }
  };

  useEffect(() => {
    fetchOperators();
  }, []);

  const fetchOperators = async () => {
    setLoading(true);
    try {
      // Verifica che il token esista
      if (!token) {
        console.error('❌ No auth token available for /api/admin/operators');
        setOperators(defaultOperators);
        return;
      }
      
      console.log('🔄 Fetching operators from:', `${API_URL}/api/admin/operators`);
      
      const res = await fetch(`${API_URL}/api/admin/operators`, {
        method: 'GET',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        }
      });
      
      const contentType = res.headers.get('content-type');
      console.log(`📡 Response status: ${res.status}, Content-Type: ${contentType}`);
      
      // Se la risposta non è OK o non è JSON, logga dettagli
      if (!res.ok || !contentType || !contentType.includes('application/json')) {
        const textBody = await res.text();
        console.error(`❌ API Error for /api/admin/operators:`);
        console.error(`   Status: ${res.status} ${res.statusText}`);
        console.error(`   Content-Type: ${contentType}`);
        console.error(`   Body (first 200 chars): ${textBody.substring(0, 200)}`);
        
        // Se è un 401/403, potrebbe essere un problema di autenticazione
        if (res.status === 401 || res.status === 403) {
          console.error('   ⚠️ Authentication issue - token may be invalid or expired');
        }
        
        setOperators(defaultOperators);
        return;
      }
      
      const data = await res.json();
      console.log(`✅ Fetched ${data.length} operators`);
      
      // Merge with role config
      const enriched = data.map(op => ({
        ...op,
        ...roleConfig[op.role],
        roleName: roleConfig[op.role]?.name || op.role,
        description: roleConfig[op.role]?.desc || ''
      }));
      setOperators(enriched.length > 0 ? enriched : defaultOperators);
    } catch (error) {
      console.error('❌ Exception fetching operators:', error.message);
      setOperators(defaultOperators);
    } finally {
      setLoading(false);
    }
  };

  const handleChangePassword = async (operator) => {
    if (!newPassword || newPassword.length < 8) {
      toast.error('La password deve essere di almeno 8 caratteri');
      return;
    }

    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/change-user-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          user_id: operator.id,
          new_password: newPassword
        })
      });

      const data = await res.json();

      if (res.ok) {
        toast.success(`Password aggiornata per ${operator.email}`);
        setEditingId(null);
        setNewPassword('');
        setShowNewPassword(false);
      } else {
        toast.error(data.detail || 'Errore nell\'aggiornamento della password');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setSaving(false);
    }
  };

  const copyToClipboard = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      toast.success('Copiato negli appunti');
    } catch (err) {
      console.error('Failed to copy: ', err);
    }
  };

  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789!@#$%';
    let password = '';
    for (let i = 0; i < 12; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPassword(password);
    setShowNewPassword(true);
  };

  const impersonateUser = async (operator) => {
    setImpersonating(operator.id);
    try {
      const res = await fetch(`${API_URL}/api/admin/impersonate`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ user_id: operator.id })
      });
      const data = await res.json();
      if (res.ok && data.access_token) {
        // Salva token admin corrente per poter tornare indietro
        localStorage.setItem('hw_admin_token_backup', token);
        localStorage.setItem('hw_token', data.access_token);
        toast.success(`Accesso come ${operator.roleName || operator.email}`);
        // Ricarica la pagina per applicare il nuovo ruolo
        window.location.href = '/dashboard';
      } else {
        toast.error(data.detail || 'Errore nell\'impersonamento');
      }
    } catch (error) {
      toast.error('Errore di connessione');
    } finally {
      setImpersonating(null);
    }
  };

  if (loading) {
    return (
      <Card className="p-6 border-0 shadow-lg">
        <div className="flex items-center justify-center h-32">
          <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
        </div>
      </Card>
    );
  }

  return (
    <Card className="p-6 border-0 shadow-lg">
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center space-x-3">
          <Users className="h-6 w-6 text-blue-900" />
          <h3 className="text-xl font-semibold text-slate-900">Credenziali Operatori</h3>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchOperators}
          className="flex items-center space-x-2"
        >
          <RefreshCw className="h-4 w-4" />
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {operators.map((operator, index) => {
          const Icon = operator.icon || Users;
          const isEditing = editingId === operator.id;
          
          return (
            <Card key={operator.id || index} className="p-4 card-hover border-0 shadow-md">
              <div className="space-y-3">
                {/* Header */}
                <div className="flex items-start justify-between">
                  <div className={`p-2 rounded-lg ${operator.color || 'bg-slate-500'}`}>
                    <Icon className="h-5 w-5 text-white" />
                  </div>
                  <Badge variant="outline" className={`text-xs ${operator.is_active !== false ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                    {operator.is_active !== false ? 'Attivo' : 'Disattivato'}
                  </Badge>
                </div>

                {/* Accedi come */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => impersonateUser(operator)}
                  disabled={impersonating === operator.id}
                  className="w-full text-xs bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-200"
                  data-testid={`impersonate-${operator.email}`}
                >
                  {impersonating === operator.id ? (
                    <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                  ) : (
                    <LogIn className="h-3 w-3 mr-1" />
                  )}
                  Accedi come
                </Button>

                {/* Role */}
                <div>
                  <h4 className="font-semibold text-slate-900 text-sm">{operator.roleName || operator.role}</h4>
                  <p className="text-xs text-slate-500 mt-1">{operator.description}</p>
                </div>

                {/* Credentials */}
                <div className="space-y-2">
                  <div>
                    <label className="text-xs font-medium text-slate-600">Email</label>
                    <div className="flex items-center justify-between bg-slate-50 rounded p-2 mt-1">
                      <span className="text-xs font-mono text-slate-700 truncate">
                        {operator.email}
                      </span>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => copyToClipboard(operator.email)}
                        className="h-6 w-6 p-0 ml-2"
                      >
                        <Copy className="h-3 w-3" />
                      </Button>
                    </div>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-600">Password</label>
                    {isEditing ? (
                      <div className="space-y-2 mt-1">
                        <div className="relative">
                          <Input
                            type={showNewPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            placeholder="Nuova password (min 8 car.)"
                            className="text-xs pr-10"
                          />
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => setShowNewPassword(!showNewPassword)}
                            className="absolute right-1 top-1/2 -translate-y-1/2 h-6 w-6 p-0"
                          >
                            {showNewPassword ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}
                          </Button>
                        </div>
                        <div className="flex gap-1">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={generatePassword}
                            className="text-xs flex-1"
                          >
                            Genera
                          </Button>
                          <Button
                            variant="default"
                            size="sm"
                            onClick={() => handleChangePassword(operator)}
                            disabled={saving || !newPassword}
                            className="bg-green-600 hover:bg-green-700 text-xs"
                          >
                            {saving ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => { setEditingId(null); setNewPassword(''); setShowNewPassword(false); }}
                            className="text-xs"
                          >
                            <X className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-between bg-slate-50 rounded p-2 mt-1">
                        <span className="text-xs font-mono text-slate-700">
                          ••••••••••
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setEditingId(operator.id)}
                          className="h-6 px-2 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          title="Modifica password"
                        >
                          <KeyRound className="h-3 w-3 mr-1" />
                          <span className="text-xs">Modifica</span>
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </Card>
          );
        })}
      </div>

      <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
        <h4 className="text-sm font-semibold text-blue-900 mb-2">Note Importanti:</h4>
        <ul className="text-xs text-blue-800 space-y-1">
          <li>• I responsabili immobili riceveranno password temporanee via email</li>
          <li>• Tutti gli operatori hanno accesso solo alle loro aree specifiche</li>
          <li>• Clicca su "Modifica" per cambiare la password di un operatore</li>
          <li>• Usa il pulsante "Genera" per creare una password sicura automaticamente</li>
        </ul>
      </div>
    </Card>
  );
};

export default OperatorCredentials;
